import { useState, useMemo, useCallback } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import { qPatientNotes, type PatientNoteRow } from '@helios/data-access';
import { formatDateShort, type PatientNoteItem } from './consultation-data';

interface NotesTabProps {
  patientId: string;
}

export function NotesTab({ patientId }: NotesTabProps) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: noteRows = [] } = usePowerSyncQuery<PatientNoteRow>(qPatientNotes(patientId));

  const notes: PatientNoteItem[] = useMemo(
    () =>
      noteRows.map((r) => ({
        id: r.id,
        patientId: r.patient_id ?? '',
        title: r.title ?? '',
        body: r.body ?? '',
        updatedAt: r.updated_at ?? r.created_at ?? '',
      })),
    [noteRows],
  );

  const [search, setSearch] = useState('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search) return notes;
    const q = search.toLowerCase();
    return notes.filter(
      (n) => n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q),
    );
  }, [notes, search]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_notes WHERE id = ?`, [id]);
      setOpenMenu(null);
    },
    [db],
  );

  const handleAddNote = useCallback(async () => {
    if (!db) return;
    const now = new Date().toISOString();
    await db.execute(
      `INSERT INTO patient_notes (id, org_id, clinic_id, patient_id, title, body, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [crypto.randomUUID(), orgId, null, patientId, 'New Note', '', now, now],
    );
  }, [db, orgId, patientId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <Button variant="gradient" size="md" onClick={handleAddNote}>
          + New Note
        </Button>
      </div>

      {/* Notes list */}
      {filtered.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sp-12)',
            color: 'var(--text-muted)',
            gap: 'var(--sp-3)',
          }}
        >
          <NavIcon name="file-text" />
          <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semi)' }}>
            No notes found
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>
            {notes.length === 0
              ? 'Create your first note to get started'
              : 'Try adjusting your search'}
          </span>
          {notes.length === 0 && (
            <Button variant="gradient" size="md" style={{ marginTop: 'var(--sp-3)' }}>
              + New Note
            </Button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {filtered.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              menuOpen={openMenu === note.id}
              onMenuToggle={() => setOpenMenu(openMenu === note.id ? null : note.id)}
              onMenuClose={() => setOpenMenu(null)}
              onDelete={() => handleDelete(note.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Note Card ───

function NoteCard({
  note,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onDelete,
}: {
  note: PatientNoteItem;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-4)',
        background: 'var(--bg-surface)',
        display: 'flex',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 4,
          }}
        >
          {note.title}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          {formatDateShort(note.updatedAt)}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--line-height-body)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {note.body}
        </div>
      </div>

      {/* Actions */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          onClick={onMenuToggle}
          style={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: menuOpen ? 'var(--bg-surface-2)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            padding: 0,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </button>

        {menuOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={onMenuClose} />
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: 4,
                width: 140,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                zIndex: 10,
                overflow: 'hidden',
              }}
            >
              {[
                { label: 'Edit', action: onMenuClose },
                { label: 'Delete', action: onDelete, danger: true },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.action}
                  style={{
                    width: '100%',
                    padding: 'var(--sp-2) var(--sp-3)',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-sm)',
                    color: item.danger ? '#dc2626' : 'var(--text)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = 'var(--bg-surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'none';
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
